import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/navigation";

import { FullScreenLeftArrowButtonIcon, FullScreenRightArrowButtonIcon } from "../../assets/icons/create-feed";
import { Media } from "../../../../types/types";

const MediaSlide = ({ media }: { media: Media }) => {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#000",
      }}
    >
      {media.type === "VIDEO" ? (
        <video
          src={media.url}
          controls
          style={{
            width: "auto",
            height: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <img
          src={media.url}
          alt={`media-${media.id}`}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
          loading="lazy"
        />
      )}
    </Box>
  );
};

const FullScreenPostModalMediaSlider = ({ media }: { media?: Media[] }) => {
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!media || media.length === 0) return null;

  return (
    <Box
      sx={{
        position: "relative",
        width: { xs: "100%", lg: "calc(100% - 480px)" },
        height: "100%",
        bgcolor: "#000",
      }}
    >
      <Swiper
        loop={false}
        modules={[Navigation]}
        onSwiper={(swiper: SwiperType) => {
          setSwiperInstance(swiper);
          swiper.on("slideChange", () => setActiveIndex(swiper.activeIndex));
        }}
        style={{ height: "100%" }}
      >
        {media.map((item) => (
          <SwiperSlide key={item.id}>
            <MediaSlide media={item} />
          </SwiperSlide>
        ))}
      </Swiper>

      <SliderNavigation swiper={swiperInstance} />

      <SliderPaginationBullets swiper={swiperInstance} count={media.length} activeIndex={activeIndex} />
    </Box>
  );
};

export default FullScreenPostModalMediaSlider;

const SliderNavigation = ({ swiper }: { swiper: any }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const handlePrev = () => {
    if (swiper) swiper.slidePrev();
  };

  const handleNext = () => {
    if (swiper) swiper.slideNext();
  };

  return (
    <>
      <IconButton
        onClick={handlePrev}
        sx={{
          position: "absolute",
          top: "50%",
          left: 16,
          transform: "translateY(-50%)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
        }}
      >
        {currentLang === "en" ? <FullScreenLeftArrowButtonIcon /> : <FullScreenRightArrowButtonIcon />}
      </IconButton>

      <IconButton
        onClick={handleNext}
        sx={{
          position: "absolute",
          top: "50%",
          right: 16,
          transform: "translateY(-50%)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
        }}
      >
        {currentLang === "en" ? <FullScreenRightArrowButtonIcon /> : <FullScreenLeftArrowButtonIcon />}
      </IconButton>
    </>
  );
};

const SliderPaginationBullets = ({
  swiper,
  count,
  activeIndex,
}: {
  swiper: any;
  count: number;
  activeIndex: number;
}) => {
  if (!swiper) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: "60px",
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: 1,
        zIndex: 10,
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          onClick={() => swiper.slideTo(index)}
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            cursor: "pointer",
            backgroundColor: index === activeIndex ? "#076D0D" : "#E4E7EC",
            transition: "background-color 0.3s",
          }}
        />
      ))}
    </Box>
  );
};
